// AGC: SiloTankDetailModal
// AGC: Full detail view for a container (Silo or Tank).
// AGC: Shows: capacity bar, current level, product, movement history.
// AGC: Actions: Add stock, Remove stock, Assign/Change product, Archive.
// AGC: Assign Product: shown when no product is assigned — goes directly to product picker.
// AGC: Change Product: shown when a product is assigned and container is empty — triggers cleaning flow.
// AGC: During cleaning and empty: user can pick and confirm the new product.

import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { clampDecimalStr } from '../../utils'
import {
  X, Database, Droplets, ArrowDownToLine, ArrowUpFromLine,
  RefreshCw, Archive, Loader2, Check, AlertTriangle, Package,
  Clock, ArrowRight, Search, Tag, Pencil,
} from 'lucide-react'
import type { Container, Warehouse, InventoryItem, InventoryMovement } from '../../types/inventory'
import type { UseWarehouseReturn } from '../../hooks/useWarehouse'
import { CapacityBar } from './CapacityBar'
import toast from 'react-hot-toast'

interface SiloTankDetailModalProps {
  isOpen: boolean
  onClose: () => void
  container: Container
  warehouse?: Warehouse
  inventoryItem?: InventoryItem
  inventoryItems?: InventoryItem[]      // AGC: Full list for product picker
  recentMovements?: InventoryMovement[]
  movementsLoading?: boolean            // AGC: Show spinner while movements are loading
  warehouseHook: UseWarehouseReturn
}

type ActionMode = 'view' | 'entry' | 'exit' | 'reassign'

const SILO_UNITS = ['ton', 'kg', 'sacas']
const TANK_UNITS = ['L', 'gal', 'mL']

function formatCanonical(value: number, displayUnit: string): string {
  const factors: Record<string, number> = {
    ton: 1000, t: 1000, sacas: 60, saca: 60, g: 0.001,
    gal: 3.785, gallon: 3.785, mL: 0.001, ml: 0.001, kg: 1, L: 1, l: 1,
  }
  const factor = factors[displayUnit] || 1
  return `${(value / factor).toFixed(2)} ${displayUnit}`
}

function formatDate(iso: string, locale?: string): string {
  return new Date(iso).toLocaleString(locale || undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export const SiloTankDetailModal: React.FC<SiloTankDetailModalProps> = ({
  isOpen,
  onClose,
  container,
  warehouse,
  inventoryItem,
  inventoryItems = [],
  recentMovements = [],
  movementsLoading = false,
  warehouseHook,
}) => {
  const { t, i18n } = useTranslation()
  const isSilo = container.type === 'silo'
  const units = isSilo ? SILO_UNITS : TANK_UNITS

  const [actionMode, setActionMode] = useState<ActionMode>('view')
  const [qty, setQty] = useState('')
  const [qtyUnit, setQtyUnit] = useState(container.capacityUnit || units[0])
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)

  // AGC: Product picker state for the reassign mode
  const [productSearch, setProductSearch] = useState('')
  const [selectedProductId, setSelectedProductId] = useState<string>(container.assignedProductId || '')
  const [selectedProductName, setSelectedProductName] = useState<string>(container.assignedProductName || '')

  // AGC: Inline label edit state
  const [isEditingLabel, setIsEditingLabel] = useState(false)
  const [labelDraft, setLabelDraft] = useState(container.label)
  const [savingLabel, setSavingLabel] = useState(false)

  // AGC: Keep labelDraft in sync if the parent swaps to a different container while modal is mounted
  useEffect(() => {
    if (!isEditingLabel) setLabelDraft(container.label)
  }, [container.label, isEditingLabel])

  // AGC: Inline capacity edit state
  const [isEditingCapacity, setIsEditingCapacity] = useState(false)
  const [capDraft, setCapDraft] = useState(String(container.capacityDisplay ?? ''))
  const [capUnitDraft, setCapUnitDraft] = useState(container.capacityUnit)
  const [savingCap, setSavingCap] = useState(false)

  // AGC: Keep capacity drafts in sync if the parent swaps to a different container
  useEffect(() => {
    if (!isEditingCapacity) {
      setCapDraft(String(container.capacityDisplay ?? ''))
      setCapUnitDraft(container.capacityUnit)
    }
  }, [container.capacityDisplay, container.capacityUnit, isEditingCapacity])

  const { recordContainerMovement, updateContainer, archiveContainer, getCapacityPercent } = warehouseHook

  const percent = getCapacityPercent(container)
  const Icon = isSilo ? Database : Droplets
  const accentColor = isSilo ? 'amber' : 'blue'

  // AGC: Filter available items for picker — deduplicate by name so duplicate DB entries
  // AGC: don't show as multiple rows. Keep the item with the highest stock when duplicates exist.
  const filteredItems = useMemo(() => {
    // AGC: Deduplicate: for each unique name keep the item with the most stock
    const byName = new Map<string, InventoryItem>()
    for (const item of inventoryItems) {
      const key = item.name?.toLowerCase().trim() || item.id
      const existing = byName.get(key)
      if (!existing || (item.quantity ?? 0) > (existing.quantity ?? 0)) {
        byName.set(key, item)
      }
    }
    const deduped = Array.from(byName.values())

    if (!productSearch.trim()) return deduped
    const q = productSearch.toLowerCase()
    return deduped.filter(i =>
      i.name?.toLowerCase().includes(q) ||
      i.category?.toLowerCase().includes(q)
    )
  }, [inventoryItems, productSearch])

  const handleMovement = useCallback(async (type: 'entry' | 'exit') => {
    const amount = parseFloat(qty)
    if (!amount || amount <= 0) { toast.error(t('inventory.container.enterValidQty')); return }
    if (!container.assignedProductId) { toast.error(t('inventory.container.assignProductFirst')); return }

    // AGC: Pre-validate overfill on frontend
    if (type === 'entry') {
      const factors: Record<string, number> = { ton: 1000, t: 1000, sacas: 60, saca: 60, g: 0.001, gal: 3.785, mL: 0.001, ml: 0.001, kg: 1, L: 1, l: 1 }
      const qtyCanonical = amount * (factors[qtyUnit] || 1)
      if (container.currentLevel + qtyCanonical > container.capacityCanonical + 0.001) {
        const free = container.capacityCanonical - container.currentLevel
        toast.error(t('inventory.container.overfillError', { free: formatCanonical(free, container.capacityUnit) }))
        return
      }
    }

    setLoading(true)
    try {
      await recordContainerMovement(container.id, {
        inventoryItemId: container.assignedProductId,
        type,
        quantityDisplay: amount,
        quantityUnit: qtyUnit,
        reason: reason || (type === 'entry' ? t('inventory.container.manualEntry') : t('inventory.container.manualExit')),
        warehouseId: container.warehouseId,
        sourceChannel: 'ui',
      })
      toast.success(type === 'entry' ? t('inventory.container.stockAdded') : t('inventory.container.stockRemoved'))
      setActionMode('view')
      setQty('')
      setReason('')
    } catch (err: any) {
      toast.error(err.message || t('inventory.container.movementFailed'))
    } finally {
      setLoading(false)
    }
  }, [qty, qtyUnit, reason, container, recordContainerMovement, t])

  // AGC: Start cleaning — only called when container already HAS a product and needs to be changed.
  // AGC: Sets status to 'cleaning', which blocks movements until a new product is assigned.
  const handleStartClean = useCallback(async () => {
    if (!window.confirm(t('inventory.container.cleaningConfirm'))) return
    setLoading(true)
    try {
      await updateContainer(container.id, { status: 'cleaning' })
      toast.success(t('inventory.container.cleaningStarted'))
      setActionMode('reassign')
    } catch (err: any) {
      toast.error(err.message || t('inventory.container.cleaningFailed'))
    } finally {
      setLoading(false)
    }
  }, [container.id, updateContainer, t])

  // AGC: Confirm product assignment — works both for first-time assign and after cleaning.
  // AGC: Clears cleaning status and sets the new product on the container.
  const handleConfirmAssign = useCallback(async () => {
    if (!selectedProductId) { toast.error(t('inventory.container.selectProductFirst')); return }
    setLoading(true)
    try {
      await updateContainer(container.id, {
        assignedProductId: selectedProductId,
        assignedProductName: selectedProductName,
        // AGC: If container was cleaning, completing the assign resets status to empty/active
        status: container.currentLevel > 0 ? 'active' : 'empty',
      })
      toast.success(t('inventory.container.productAssigned', { name: selectedProductName }))
      setActionMode('view')
      setProductSearch('')
    } catch (err: any) {
      toast.error(err.message || t('inventory.container.assignFailed'))
    } finally {
      setLoading(false)
    }
  }, [container.id, container.currentLevel, selectedProductId, selectedProductName, updateContainer, t])

  const handleArchive = useCallback(async () => {
    if (container.currentLevel > 0) { toast.error(t('inventory.container.cannotArchiveWithStock')); return }
    if (!window.confirm(t('inventory.container.archiveConfirm', { label: container.label }))) return
    setLoading(true)
    try {
      await archiveContainer(container.id)
      toast.success(t('inventory.container.archived'))
      onClose()
    } catch (err: any) {
      toast.error(err.message || t('inventory.container.archiveFailed'))
    } finally {
      setLoading(false)
    }
  }, [container, archiveContainer, onClose, t])

  // AGC: Save inline label edit — optimistic with rollback on failure
  const saveLabel = useCallback(async () => {
    if (savingLabel) return
    const trimmed = labelDraft.trim()
    if (!trimmed || trimmed === container.label) {
      setIsEditingLabel(false)
      setLabelDraft(container.label)
      return
    }
    setSavingLabel(true)
    try {
      await updateContainer(container.id, { label: trimmed })
      setIsEditingLabel(false)
    } catch (err) {
      console.error('AGC: label save failed', err)
      toast.error(t('inventory.container.labelSaveError'))
      setLabelDraft(container.label)
    } finally {
      setSavingLabel(false)
    }
  }, [labelDraft, container.label, container.id, updateContainer, t, savingLabel])

  const cancelLabelEdit = useCallback(() => {
    setIsEditingLabel(false)
    setLabelDraft(container.label)
  }, [container.label])

  // AGC: Save inline capacity edit — validates positive number, reuses existing unit validator on backend
  const saveCapacity = useCallback(async () => {
    if (savingCap) return
    // AGC: Number() (not parseFloat) rejects trailing non-numeric garbage like "500abc"
    const value = Number(capDraft)
    if (!isFinite(value) || value <= 0) {
      setIsEditingCapacity(false)
      setCapDraft(String(container.capacityDisplay ?? ''))
      setCapUnitDraft(container.capacityUnit)
      return
    }
    // AGC: Tolerance-based compare — capacityDisplay is a float, exact === is unreliable
    const noChange = Math.abs(value - (container.capacityDisplay ?? 0)) < 1e-9 && capUnitDraft === container.capacityUnit
    if (noChange) {
      setIsEditingCapacity(false)
      return
    }
    setSavingCap(true)
    try {
      await updateContainer(container.id, { capacityValue: value, capacityUnit: capUnitDraft })
      setIsEditingCapacity(false)
    } catch (err) {
      console.error('AGC: capacity save failed', err)
      toast.error(t('inventory.container.capacitySaveError'))
      setCapDraft(String(container.capacityDisplay ?? ''))
      setCapUnitDraft(container.capacityUnit)
    } finally {
      setSavingCap(false)
    }
  }, [capDraft, capUnitDraft, container.capacityDisplay, container.capacityUnit, container.id, updateContainer, t, savingCap])

  const cancelCapacityEdit = useCallback(() => {
    setIsEditingCapacity(false)
    setCapDraft(String(container.capacityDisplay ?? ''))
    setCapUnitDraft(container.capacityUnit)
  }, [container.capacityDisplay, container.capacityUnit])

  if (!isOpen) return null

  // AGC: Determine which action buttons to show based on container state
  const hasProduct = !!container.assignedProductId
  const isCleaning = container.status === 'cleaning'
  const isEmpty = container.currentLevel <= 0

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} role="button" aria-label="Close" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClose()} />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* AGC: Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg bg-${accentColor}-100`}>
              <Icon className={`w-5 h-5 text-${accentColor}-600`} />
            </div>
            <div>
              {isEditingLabel ? (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={labelDraft}
                    onChange={(e) => setLabelDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveLabel()
                      if (e.key === 'Escape') cancelLabelEdit()
                    }}
                    onBlur={saveLabel}
                    maxLength={60}
                    autoFocus
                    aria-label={t('inventory.container.editLabel')}
                    disabled={savingLabel}
                    className="font-semibold text-gray-900 bg-white border border-gray-300 rounded px-2 py-0.5 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  {savingLabel && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { setLabelDraft(container.label); setIsEditingLabel(true) }}
                  className="flex items-center gap-1 group"
                  aria-label={t('inventory.container.editLabel')}
                >
                  <h2 className="font-semibold text-gray-900">{container.label}</h2>
                  <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              )}
              {warehouse && <p className="text-xs text-gray-500">{warehouse.name}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!isCleaning && container.status !== 'maintenance' && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                container.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {container.status}
              </span>
            )}
            {(isCleaning || container.status === 'maintenance') && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                {container.status}
              </span>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 ml-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* AGC: Capacity section */}
          <div className="p-4 border-b border-gray-50">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('inventory.container.capacity')}</p>
                {isEditingCapacity ? (
                  <div className="flex items-center gap-1 mt-0.5">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={capDraft}
                      onChange={(e) => setCapDraft(clampDecimalStr(e.target.value))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveCapacity()
                        if (e.key === 'Escape') cancelCapacityEdit()
                      }}
                      autoFocus
                      aria-label={t('inventory.container.editCapacity')}
                      disabled={savingCap}
                      className="w-20 text-xs border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <select
                      value={capUnitDraft}
                      onChange={(e) => setCapUnitDraft(e.target.value)}
                      disabled={savingCap}
                      className="text-xs border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      {units.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <button type="button" onClick={saveCapacity} disabled={savingCap} className="p-0.5 text-green-600 hover:bg-green-50 rounded" aria-label="Save">
                      {savingCap ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    </button>
                    <button type="button" onClick={cancelCapacityEdit} disabled={savingCap} className="p-0.5 text-gray-500 hover:bg-gray-100 rounded" aria-label="Cancel">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsEditingCapacity(true)}
                    className="flex items-center gap-1 group"
                    aria-label={t('inventory.container.editCapacity')}
                  >
                    <p className="text-xs text-gray-500">
                      {formatCanonical(container.currentLevel, container.capacityUnit)} / {formatCanonical(container.capacityCanonical, container.capacityUnit)}
                    </p>
                    <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                )}
              </div>
              <p className={`text-2xl font-bold ${percent >= 90 ? 'text-red-600' : percent >= 70 ? 'text-amber-600' : 'text-green-600'}`}>
                {percent.toFixed(0)}%
              </p>
            </div>
            <CapacityBar
              currentLevel={container.currentLevel}
              capacity={container.capacityCanonical}
              displayUnit={container.capacityUnit}
              showToggle
              showLabels={false}
            />

            {/* AGC: Assigned product */}
            <div className="mt-3 flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-400" />
              {inventoryItem ? (
                <span className="text-sm font-medium text-gray-700">{inventoryItem.name}</span>
              ) : container.assignedProductName ? (
                <span className="text-sm text-gray-700">{container.assignedProductName}</span>
              ) : (
                <span className="text-sm text-gray-400 italic">{t('inventory.container.noProductAssigned')}</span>
              )}
            </div>
          </div>

          {/* AGC: Action area */}
          {container.status !== 'maintenance' && (
            <div className="p-4 border-b border-gray-50">

              {/* ──────────────────────────────────────────────────── */}
              {/* AGC: VIEW MODE — main action buttons */}
              {/* ──────────────────────────────────────────────────── */}
              {actionMode === 'view' && (
                <div className="space-y-3">
                  <div className="flex gap-2 flex-wrap">
                    {/* AGC: Add Stock — only when product is assigned and not cleaning */}
                    <button
                      onClick={() => setActionMode('entry')}
                      disabled={isCleaning || !hasProduct}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 text-green-700 text-sm font-medium hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ArrowDownToLine className="w-4 h-4" />
                      {t('inventory.container.addStock')}
                    </button>

                    {/* AGC: Remove Stock — only when product assigned, not cleaning, and has stock */}
                    <button
                      onClick={() => setActionMode('exit')}
                      disabled={isCleaning || !hasProduct || container.currentLevel <= 0}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ArrowUpFromLine className="w-4 h-4" />
                      {t('inventory.container.removeStock')}
                    </button>

                    {/* AGC: Assign Product — when NO product assigned yet (first time) */}
                    {!hasProduct && !isCleaning && (
                      <button
                        onClick={() => {
                          setSelectedProductId('')
                          setSelectedProductName('')
                          setProductSearch('')
                          setActionMode('reassign')
                        }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors"
                      >
                        <Tag className="w-4 h-4" />
                        {t('inventory.container.assignProduct')}
                      </button>
                    )}

                    {/* AGC: Change Product — only when product IS assigned, container empty, and active */}
                    {hasProduct && isEmpty && !isCleaning && (
                      <button
                        onClick={handleStartClean}
                        disabled={loading}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors"
                      >
                        <RefreshCw className="w-4 h-4" />
                        {t('inventory.container.startCleaning')}
                      </button>
                    )}

                    {/* AGC: Archive */}
                    <button
                      onClick={handleArchive}
                      disabled={container.currentLevel > 0}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-50 text-gray-500 text-sm font-medium hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ml-auto"
                    >
                      <Archive className="w-4 h-4" />
                      {t('inventory.container.archiveContainer')}
                    </button>
                  </div>

                  {/* AGC: Cleaning banner — when cleaning and NOT empty yet */}
                  {isCleaning && !isEmpty && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">{t('inventory.container.cleaningInProgress')}</p>
                        <p className="text-xs text-amber-600">{t('inventory.container.cleaningDescription')}</p>
                      </div>
                    </div>
                  )}

                  {/* AGC: Cleaning done banner — cleaning and container IS empty, ready to assign */}
                  {isCleaning && isEmpty && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
                      <Check className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800">{t('inventory.container.cleaningDoneTitle')}</p>
                        <p className="text-xs text-blue-600 mb-2">{t('inventory.container.cleaningDoneDesc')}</p>
                        <button
                          onClick={() => {
                            setSelectedProductId('')
                            setSelectedProductName('')
                            setProductSearch('')
                            setActionMode('reassign')
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
                        >
                          <Tag className="w-3.5 h-3.5" />
                          {t('inventory.container.assignNewProduct')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ──────────────────────────────────────────────────── */}
              {/* AGC: ENTRY / EXIT FORM */}
              {/* ──────────────────────────────────────────────────── */}
              {(actionMode === 'entry' || actionMode === 'exit') && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setActionMode('view')} className="text-sm text-gray-500 hover:text-gray-700">← {t('common.back')}</button>
                    <h3 className="text-sm font-semibold text-gray-800">
                      {actionMode === 'entry' ? t('inventory.container.addStockTitle') : t('inventory.container.removeStockTitle')}
                    </h3>
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={qty}
                      onChange={e => setQty(clampDecimalStr(e.target.value))}
                      placeholder={t('inventory.container.quantityPlaceholder')}
                      min={0.001}
                      step="0.01"
                      autoFocus
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <select
                      value={qtyUnit}
                      onChange={e => setQtyUnit(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {units.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>

                  <input
                    type="text"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder={t('inventory.container.reasonPlaceholder')}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />

                  <button
                    onClick={() => handleMovement(actionMode as 'entry' | 'exit')}
                    disabled={loading || !qty || parseFloat(qty) <= 0}
                    className={`w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors ${
                      actionMode === 'entry'
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {actionMode === 'entry' ? t('inventory.container.confirmEntry') : t('inventory.container.confirmExit')}
                  </button>
                </div>
              )}

              {/* ──────────────────────────────────────────────────── */}
              {/* AGC: REASSIGN / ASSIGN PRODUCT FORM */}
              {/* ──────────────────────────────────────────────────── */}
              {actionMode === 'reassign' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setActionMode('view')} className="text-sm text-gray-500 hover:text-gray-700">← {t('common.back')}</button>
                    <h3 className="text-sm font-semibold text-gray-800">
                      {hasProduct ? t('inventory.container.assignNewProduct') : t('inventory.container.assignProduct')}
                    </h3>
                  </div>

                  {/* AGC: Search input */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={e => setProductSearch(e.target.value)}
                      placeholder={t('inventory.container.searchProducts')}
                      autoFocus
                      className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* AGC: Product list */}
                  <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {filteredItems.length === 0 ? (
                      <p className="text-sm text-gray-400 italic p-3 text-center">{t('inventory.container.noItemsFound')}</p>
                    ) : filteredItems.map(item => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setSelectedProductId(item.id)
                          setSelectedProductName(item.name)
                        }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          selectedProductId === item.id
                            ? 'bg-blue-50 text-blue-800 font-medium'
                            : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span>{item.name}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            {/* AGC: Show current stock so user can confirm it's the right item */}
                            {typeof item.quantity === 'number' && (
                              <span className="text-xs text-emerald-600 font-medium">
                                {item.quantity.toFixed(1)} {item.unit}
                              </span>
                            )}
                            {item.category && (
                              <span className="text-xs text-gray-400">{item.category}</span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* AGC: Selected product confirmation */}
                  {selectedProductId && (
                    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-50 border border-blue-200">
                      <Tag className="w-4 h-4 text-blue-600 shrink-0" />
                      <span className="text-sm font-medium text-blue-800 flex-1">{selectedProductName}</span>
                      <button
                        type="button"
                        onClick={() => { setSelectedProductId(''); setSelectedProductName('') }}
                        className="text-xs text-blue-500 hover:text-blue-700"
                      >
                        {t('common.clear')}
                      </button>
                    </div>
                  )}

                  <button
                    onClick={handleConfirmAssign}
                    disabled={loading || !selectedProductId}
                    className="w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {t('inventory.container.confirmAssignment')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* AGC: Movement history */}
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-gray-400" />
              {t('inventory.container.recentMovements')}
            </h3>
            {movementsLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : recentMovements.length === 0 ? (
              <p className="text-sm text-gray-400 italic text-center py-4">{t('inventory.container.noMovements')}</p>
            ) : (
              <div className="space-y-2">
                {recentMovements.map(mv => (
                  <div key={mv.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50">
                    <div className={`p-1.5 rounded-full ${mv.type === 'entry' || mv.type === 'transfer_in' ? 'bg-green-100' : 'bg-red-100'}`}>
                      {mv.type === 'entry' || mv.type === 'transfer_in'
                        ? <ArrowDownToLine className="w-3.5 h-3.5 text-green-600" />
                        : <ArrowUpFromLine className="w-3.5 h-3.5 text-red-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">
                        {mv.type === 'entry' || mv.type === 'transfer_in' ? '+' : ''}{Math.abs(mv.quantity).toFixed(2)} {mv.quantityUnit || container.capacityUnit}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{mv.reason || mv.type}</p>
                    </div>
                    {mv.transferId && (
                      <ArrowRight className="w-3.5 h-3.5 text-blue-400 shrink-0" title={`Transfer: ${mv.transferId}`} />
                    )}
                    <p className="text-xs text-gray-400 shrink-0">{formatDate(mv.createdAt, i18n.language)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SiloTankDetailModal
